import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { CalendarDays, Plus, Trash2, X, Users, AlertTriangle, Pencil, Eye, Send, Undo2 } from 'lucide-react';
import { formatDateShort } from '../lib/api';

const EMPTY_FORM = {
  name: '', description: '', date: '', start_time: '19:00', end_time: '22:00',
  prep_minutes: 30, cleanup_minutes: 30, table_ids: [], capacity_override: '',
  status: 'draft', online_bookable: true,
};

const STATUS_META = {
  draft: { label: 'Entwurf', cls: 'bg-[#EAB308]/15 text-[#B45309]' },
  published: { label: 'Veröffentlicht', cls: 'bg-[#16A34A]/15 text-[#16A34A]' },
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [checking, setChecking] = useState(false);
  const [bookingsFor, setBookingsFor] = useState(null);
  const [bookings, setBookings] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, tb, ar] = await Promise.all([
        api.get('/events'),
        api.get('/tables'),
        api.get('/areas'),
      ]);
      setEvents(ev.data?.events || []);
      setTables(Array.isArray(tb.data) ? tb.data : (tb.data?.tables || []));
      setAreas(Array.isArray(ar.data) ? ar.data : (ar.data?.areas || []));
    } catch {
      toast.error('Events konnten nicht geladen werden.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tablesByArea = useMemo(() => {
    const areaName = Object.fromEntries(areas.map(a => [a.id, a.name]));
    const groups = {};
    for (const t of tables) {
      const key = t.area_id || '_none';
      if (!groups[key]) groups[key] = { name: areaName[key] || 'Ohne Bereich', tables: [] };
      groups[key].tables.push(t);
    }
    Object.values(groups).forEach(g => g.tables.sort((a, b) => String(a.table_number).localeCompare(String(b.table_number), 'de', { numeric: true })));
    return groups;
  }, [tables, areas]);

  const theoretical = useMemo(() => {
    const byId = Object.fromEntries(tables.map(t => [t.id, t]));
    return (form.table_ids || []).reduce((s, id) => s + Number(byId[id]?.max_persons || 0), 0);
  }, [form.table_ids, tables]);

  const openCreate = () => {
    setEditId(null); setForm(EMPTY_FORM); setConflicts(null); setShowForm(true);
  };

  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      name: e.name || '', description: e.description || '', date: e.date || '',
      start_time: e.start_time || '19:00', end_time: e.end_time || '22:00',
      prep_minutes: e.prep_minutes ?? 30, cleanup_minutes: e.cleanup_minutes ?? 30,
      table_ids: e.table_ids || [], capacity_override: e.capacity_override ?? '',
      status: e.status || 'draft', online_bookable: e.online_bookable !== false,
    });
    setConflicts(null); setShowForm(true);
  };

  const toggleTable = (id) => {
    setForm(f => ({
      ...f,
      table_ids: f.table_ids.includes(id) ? f.table_ids.filter(x => x !== id) : [...f.table_ids, id],
    }));
    setConflicts(null);
  };

  const payload = (statusOverride) => ({
    name: form.name.trim(),
    description: form.description.trim(),
    date: form.date,
    start_time: form.start_time,
    end_time: form.end_time,
    prep_minutes: Number(form.prep_minutes || 0),
    cleanup_minutes: Number(form.cleanup_minutes || 0),
    table_ids: form.table_ids,
    capacity_override: form.capacity_override === '' ? null : Number(form.capacity_override),
    status: statusOverride || form.status,
    online_bookable: !!form.online_bookable,
  });

  const validate = () => {
    if (!form.name.trim()) { toast.error('Bitte einen Event-Namen angeben.'); return false; }
    if (!form.date) { toast.error('Bitte ein Datum wählen.'); return false; }
    if (form.end_time <= form.start_time) { toast.error('Die Endzeit muss nach der Startzeit liegen.'); return false; }
    if (form.table_ids.length === 0) { toast.error('Bitte mindestens einen Tisch auswählen.'); return false; }
    return true;
  };

  const checkConflicts = async () => {
    if (form.table_ids.length === 0) { toast.error('Bitte zuerst Tische auswählen.'); return; }
    setChecking(true);
    try {
      const r = await api.post('/events/conflict-check', payload());
      setConflicts(r.data?.conflicts || []);
      if ((r.data?.conflicts || []).length === 0) toast.success('Keine Konflikte – alle Tische frei.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konfliktprüfung fehlgeschlagen.');
    } finally { setChecking(false); }
  };

  const submit = async (statusOverride) => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/events/${editId}`, payload(statusOverride));
        toast.success('Event aktualisiert.');
      } else {
        await api.post('/events', payload(statusOverride));
        toast.success('Event angelegt.');
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally { setSaving(false); }
  };

  const togglePublish = async (e) => {
    const next = e.status === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/events/${e.id}`, { status: next });
      toast.success(next === 'published' ? 'Event veröffentlicht.' : 'Event zurückgezogen.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen.');
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Event „${e.name}" wirklich löschen?`)) return;
    try { await api.delete(`/events/${e.id}`); toast.success('Event gelöscht.'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen.'); }
  };

  const openBookings = async (e) => {
    setBookingsFor(e); setBookings([]);
    try { const r = await api.get(`/events/${e.id}/bookings`); setBookings(r.data?.bookings || []); }
    catch { toast.error('Buchungen konnten nicht geladen werden.'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-events">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-brand flex items-center gap-2"><CalendarDays className="w-6 h-6 text-[#EAB308]" /> Events</h1>
          <p className="text-sm text-[#5C5C59] mt-1">Sonder-Events (z. B. Weinprobe) für einzeln ausgewählte Tische anlegen. Die gewählten Tische werden für normale Reservierungen gesperrt.</p>
        </div>
        <button onClick={openCreate} data-testid="event-add-btn" className="btn-primary px-4 py-2 rounded flex items-center gap-2">
          <Plus className="w-4 h-4" /> Event anlegen
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#9CA3AF]">Lädt…</div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-[#9CA3AF]" data-testid="event-empty">Noch keine Events angelegt.</div>
        ) : (
          <div className="divide-y divide-[#F0F0EC]">
            {events.map(e => {
              const meta = STATUS_META[e.status] || STATUS_META.draft;
              return (
                <div key={e.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`event-row-${e.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#1C1C19]">{e.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${meta.cls}`}>{meta.label}</span>
                      {e.online_bookable && <span className="text-xs px-2 py-0.5 rounded bg-[#3B82F6]/15 text-[#3B82F6] font-medium">Online buchbar</span>}
                    </div>
                    <div className="text-sm text-[#5C5C59] mt-0.5">
                      {formatDateShort(e.date)} · {e.start_time}–{e.end_time} · {(e.table_ids || []).length} Tisch(e)
                    </div>
                    {e.description && <div className="text-xs text-[#9CA3AF] mt-0.5 line-clamp-1">{e.description}</div>}
                  </div>
                  <div className="text-right min-w-[120px]">
                    <div className="font-semibold flex items-center gap-1 justify-end"><Users className="w-4 h-4 text-[#9CA3AF]" /> {e.booked_seats}/{e.capacity}</div>
                    <div className="text-xs text-[#16A34A]">{e.available_seats} frei</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => togglePublish(e)} data-testid={`event-publish-${e.id}`} title={e.status === 'published' ? 'Zurückziehen' : 'Veröffentlichen'}
                      className={`px-2.5 py-1.5 text-sm rounded border flex items-center gap-1 ${e.status === 'published' ? 'border-[#B45309] text-[#B45309] hover:bg-[#EAB308]/10' : 'border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A]/10'}`}>
                      {e.status === 'published' ? <><Undo2 className="w-3.5 h-3.5" /> Zurückziehen</> : <><Send className="w-3.5 h-3.5" /> Veröffentlichen</>}
                    </button>
                    <button onClick={() => openBookings(e)} data-testid={`event-bookings-${e.id}`} title="Buchungen" className="p-1.5 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(e)} data-testid={`event-edit-${e.id}`} title="Bearbeiten" className="p-1.5 text-[#5C5C59] hover:bg-[#F0F0EC] rounded"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(e)} data-testid={`event-delete-${e.id}`} title="Löschen" className="p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={ev => ev.stopPropagation()} data-testid="event-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">{editId ? 'Event bearbeiten' : 'Neues Event'}</h2>
              <button onClick={() => setShowForm(false)} className="text-[#9CA3AF] hover:text-[#1C1C19]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Event-Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="event-form-name" placeholder="z. B. Weinprobe mit Winzer" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Beschreibung</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="event-form-description" rows={2} placeholder="Kurzbeschreibung für Gäste (optional)" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Datum *</label>
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="event-form-date" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Start *</label>
                  <input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="event-form-start" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Ende *</label>
                  <input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} data-testid="event-form-end" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Vorbereitung (Min.)</label>
                  <input type="number" min="0" value={form.prep_minutes} onChange={e => setForm({ ...form, prep_minutes: e.target.value })} data-testid="event-form-prep" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Reinigung (Min.)</label>
                  <input type="number" min="0" value={form.cleanup_minutes} onChange={e => setForm({ ...form, cleanup_minutes: e.target.value })} data-testid="event-form-cleanup" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Tische auswählen *</label>
                  <span className="text-xs text-[#5C5C59]" data-testid="event-form-theoretical">Kapazität: <b>{theoretical}</b> Plätze · {form.table_ids.length} Tisch(e)</span>
                </div>
                <div className="border border-[#E5E5E0] rounded p-3 space-y-3 max-h-52 overflow-y-auto" data-testid="event-form-tables">
                  {Object.entries(tablesByArea).map(([aid, grp]) => (
                    <div key={aid}>
                      <div className="text-xs font-semibold text-[#9CA3AF] uppercase mb-1">{grp.name}</div>
                      <div className="flex flex-wrap gap-2">
                        {grp.tables.map(t => {
                          const sel = form.table_ids.includes(t.id);
                          return (
                            <button key={t.id} type="button" onClick={() => toggleTable(t.id)} data-testid={`event-table-${t.id}`}
                              className={`px-3 py-1.5 rounded border text-sm transition-colors ${sel ? 'bg-[#EAB308] border-[#EAB308] text-white' : 'bg-white border-[#E5E5E0] text-[#5C5C59] hover:border-[#EAB308]'}`}>
                              Tisch {t.table_number} <span className="opacity-70">({t.max_persons}P)</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="text-sm font-medium block mb-1">Verkaufskapazität (optional)</label>
                  <input type="number" min="0" value={form.capacity_override} onChange={e => setForm({ ...form, capacity_override: e.target.value })} data-testid="event-form-capacity" placeholder={`Standard: ${theoretical}`} className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                  <p className="text-xs text-[#9CA3AF] mt-1">Leer = volle Tischkapazität ({theoretical}). Darf nicht höher sein.</p>
                </div>
                <label className="flex items-center gap-2 pb-2 cursor-pointer">
                  <input type="checkbox" checked={form.online_bookable} onChange={e => setForm({ ...form, online_bookable: e.target.checked })} data-testid="event-form-online" className="w-4 h-4 accent-[#EAB308]" />
                  <span className="text-sm">Online buchbar</span>
                </label>
              </div>

              <button onClick={checkConflicts} disabled={checking} data-testid="event-form-check" className="w-full py-2 rounded border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 text-sm flex items-center justify-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {checking ? 'Prüft…' : 'Konflikte prüfen'}
              </button>
              {conflicts !== null && (
                <div data-testid="event-form-conflicts" className={`rounded p-3 text-sm ${conflicts.length === 0 ? 'bg-[#16A34A]/10 text-[#16A34A]' : 'bg-[#DC2626]/10 text-[#DC2626]'}`}>
                  {conflicts.length === 0 ? 'Keine Konflikte – alle gewählten Tische sind im Zeitfenster frei.' : (
                    <div className="space-y-1">
                      <div className="font-medium">{conflicts.length} Konflikt(e) gefunden:</div>
                      {conflicts.map((c, i) => (
                        <div key={i}>• Tisch belegt {c.start_time}–{c.end_time}: {c.customer_name || (c.type === 'walk_in' ? 'Walk-in' : 'Reservierung')} ({c.persons}P)</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 min-w-[100px] py-2 rounded border border-[#E5E5E0]">Abbrechen</button>
              <button onClick={() => submit('draft')} disabled={saving} data-testid="event-form-save-draft" className="flex-1 min-w-[120px] py-2 rounded border border-[#EAB308] text-[#B45309] hover:bg-[#EAB308]/10">{saving ? 'Speichert…' : 'Als Entwurf speichern'}</button>
              <button onClick={() => submit('published')} disabled={saving} data-testid="event-form-save-publish" className="flex-1 min-w-[120px] btn-primary py-2 rounded">{saving ? 'Speichert…' : 'Veröffentlichen'}</button>
            </div>
          </div>
        </div>
      )}

      {bookingsFor && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setBookingsFor(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="event-bookings-dialog">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-medium">Buchungen · {bookingsFor.name}</h2>
              <button onClick={() => setBookingsFor(null)} className="text-[#9CA3AF] hover:text-[#1C1C19]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#5C5C59] mb-4">{bookingsFor.booked_seats}/{bookingsFor.capacity} Plätze gebucht · {bookingsFor.available_seats} frei</p>
            {bookings.length === 0 ? (
              <div className="p-6 text-center text-[#9CA3AF]">Noch keine Buchungen.</div>
            ) : (
              <div className="divide-y divide-[#F0F0EC]">
                {bookings.map(b => (
                  <div key={b.id} className="py-2.5 flex items-center justify-between" data-testid={`event-booking-${b.id}`}>
                    <div>
                      <div className="font-medium text-[#1C1C19]">{b.customer_name}</div>
                      <div className="text-xs text-[#9CA3AF]">{b.email}{b.phone ? ` · ${b.phone}` : ''}</div>
                      {b.note && <div className="text-xs text-[#9CA3AF] mt-0.5">{b.note}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-semibold flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#9CA3AF]" /> {b.persons}</div>
                      <div className="text-xs text-[#9CA3AF]">{b.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
